import { ExclamationCircleFilled, PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Table } from "antd";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import AddEtablissement from "./AddEtablissement";
import ActionDropdown from "../../production/components/ActionDropdown";
import axiosInstance from "../../../variables/api/axiosInstance";

const { confirm } = Modal;

const Etablissements = () => {
  const [etablissements, setEtablissements] = useState([]);
  const [typesLookup, setTypesLookup] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEtablissement, setSelectedEtablissement] = useState(null);

  const fetchTypes = () => {
    axiosInstance
      .get(`/api/parametre/type/TYPE_ETABLISSEMENT`)
      .then((res) => {
        const lookup = {};
        (res.data || []).forEach((type) => {
          const id = parseInt(type.parametreId ?? type.id, 10);
          if (!Number.isNaN(id)) {
            lookup[id] = type.libelleParametre || type.libelle || type.parametre;
          }
        });
        setTypesLookup(lookup);
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des types d'établissement :", err);
        toast.error("Erreur lors du chargement des types d'établissement");
      });
  };

  const fetchEtablissements = () => {
    axiosInstance
      .get(`/api/etablissement`)
      .then((res) => {
        const sorted = res.data.sort((a, b) => b.codeId.localeCompare(a.codeId));
        setEtablissements(sorted);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des établissements");
      });
  };

  useEffect(() => {
    fetchTypes();
    fetchEtablissements();
  }, []);

  const handleShow = (edit = false, etablissement = null) => {
    setEditMode(edit);
    setSelectedEtablissement(etablissement);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    fetchEtablissements();
  };

  const handleAdd = (newData) => {
    setEtablissements((prev) => {
      const exists = prev.some((et) => et.codeId === newData.codeId);
      if (exists) return prev;
      return [newData, ...prev].sort((a, b) => b.codeId.localeCompare(a.codeId));
    });
  };

  const handleUpdate = (updatedData) => {
    setEtablissements((prev) =>
      prev
        .map((et) => (et.codeId === updatedData.codeId ? updatedData : et))
        .sort((a, b) => b.codeId.localeCompare(a.codeId))
    );
  };

  const showDeleteConfirm = (codeId) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer cet établissement ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => deleteEtablissement(codeId),
    });
  };

  const deleteEtablissement = (codeId) => {
    axiosInstance
      .delete(`/api/etablissement/${codeId}`)
      .then(() => {
        toast.success("Établissement supprimé avec succès");
        setEtablissements((prev) => prev.filter((et) => et.codeId !== codeId));
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors de la suppression");
      });
  };

  const columns = [
    { title: "Nom", dataIndex: "nom", key: "nom" },
    { title: "Adresse", dataIndex: "adresse", key: "adresse" },
    {
      title: "Type",
      dataIndex: "typeEtablissement",
      key: "typeEtablissement",
      render: (value) => {
        const directLabel = value?.libelleParametre;
        const id =
          typeof value === "object" ? value?.parametreId ?? value?.id : value;
        const mappedLabel =
          id && !Number.isNaN(parseInt(id, 10))
            ? typesLookup[parseInt(id, 10)]
            : null;

        return directLabel || mappedLabel || "—";
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <i className="bx bx-edit-alt" />,
              onClick: () => handleShow(true, record),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <i className="bx bx-trash" />,
              danger: true,
              onClick: () => showDeleteConfirm(record.codeId),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="ETABLISSEMENTS" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleShow(false)}
              >
                Ajouter un établissement
              </Button>
              <div className="card">
                <Table
                  columns={columns}
                  dataSource={etablissements}
                  rowKey="codeId"
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                />
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      {showModal && (
        <AddEtablissement
          show={showModal}
          handleClose={handleClose}
          editMode={editMode}
          selectedEtablissement={selectedEtablissement}
          etablissements={etablissements}
          onAdd={handleAdd}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
};

export default Etablissements;
